/* eslint-disable spaced-comment */
import React from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/css';

import { noop } from '@ajaibid/utils';
import { B400, BackgroundDisabled, N0, N100 } from '@/colors/colorsV1';

const styInputToggle = css`
  --thumb-size: 1rem;
  --thumb: ${N0};
  --thumb-disabled: ${N100};
  --thumb-highlight: hsl(0 0% 0% / 25%);

  --track-size: calc(var(--thumb-size) * 2);
  --track-padding: 2px;
  --track-inactive: ${N100};
  --track-active: ${B400};
  --track-disabled: ${BackgroundDisabled};

  --thumb-color: var(--thumb);
  --thumb-color-highlight: var(--thumb-highlight);
  --track-color: var(--track-inactive);

  --thumb-position: 0%;
  --thumb-transition-duration: 0.25s;
  --highlight-size: 0;

  display: flex;
  align-items: center;
  gap: 1ch;
  justify-content: space-between;

  cursor: pointer;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  margin: unset;

  span {
    font-size: 1rem;
  }

  input[type='checkbox'] {
    padding: var(--track-padding);
    background: var(--track-color);
    inline-size: var(--track-size);
    block-size: var(--thumb-size);
    border-radius: var(--track-size);

    appearance: none;
    touch-action: pan-y;
    border: none;
    outline-offset: 5px;
    box-sizing: content-box;

    flex-shrink: 0;
    display: grid;
    align-items: center;
    grid: [track] 1fr / [track] 1fr;

    cursor: pointer;
    transition: background-color 0.25s ease;

    &::before {
      content: '';
      grid-area: track;
      inline-size: var(--thumb-size);
      block-size: var(--thumb-size);
      background: var(--thumb-color);
      box-shadow: 0 0 0 var(--highlight-size) var(--thumb-color-highlight);
      border-radius: 50%;
      transform: translateX(var(--thumb-position));
      transition: transform var(--thumb-transition-duration) ease, box-shadow 0.25s ease;
    }
  }

  input[type='checkbox']:checked {
    --track-color: var(--track-active);

    --thumb-position: 100%;
  }

  &:hover input[type='checkbox']::before {
    --highlight-size: 0.25rem;
  }

  input[type='checkbox']:disabled {
    cursor: not-allowed;
    --track-color: var(--track-disabled);

    &::before {
      --thumb-color: var(--thumb-disabled);
      cursor: not-allowed;
      box-shadow: none;
    }
  }
`;

/**
 * @typedef {'left' | 'right'} labelPosition
 */

/**
 *
 * @typedef   {object}          InputToggleProps
 *
 * @property  {boolean}         isChecked
 * @property  {function}        onChange
 * @property  {boolean}         disabled
 * @property  {string}          label
 * @property  {labelPosition}   labelPosition
 * @property  {string}          name
 *
 */

const InputToggle = ({ isChecked, onChange, disabled, label, labelPosition, name }) => {
  return (
    <label htmlFor={name} className={styInputToggle}>
      <span>{label && labelPosition === 'left' && label}</span>
      <input
        type="checkbox"
        id={name}
        checked={isChecked}
        onChange={onChange}
        disabled={disabled}
      />
      <span>{label && labelPosition === 'right' && label}</span>
    </label>
  );
};

InputToggle.displayName = 'InputToggle';

InputToggle.propTypes = {
  isChecked: PropTypes.bool,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  labelPosition: PropTypes.oneOf(['left', 'right']),
  name: PropTypes.string,
};

InputToggle.defaultProps = {
  isChecked: false,
  onChange: noop,
  disabled: false,
  label: '',
  labelPosition: 'right',
  name: '',
};

export default InputToggle;
